import i18n from "../i18n";
import { numberFormat } from "src/utils";
import { moduleSettings } from "../stores/module-settings.store";
import { Commercial, CommercialTranslation, PrepaymentInfos } from "../types";
import { getLanguageSwitcherService } from "./language-switcher.service";

export class CommercialService {
  private _commercial: Commercial;
  private _translationInCurrentLocale: CommercialTranslation | undefined;
  private _translationInRestaurantLocale: CommercialTranslation | undefined;

  constructor(commercial: Commercial, public locale: string) {
    this._commercial = commercial;
    this.locale = getLanguageSwitcherService().golLocaleFromSwitcher(locale);
    this._translationInCurrentLocale =
      this.loadCommercialTranslationForLocale();
    this._translationInRestaurantLocale =
      this.loadCommercialTranslationForLocale(
        moduleSettings.getDefaultLocale()
      );
  }
  get image(): string | undefined {
    return this._commercial.image;
  }

  get description(): string {
    return (
      this.loadInCurrentLocale("description", this) ||
      this.loadInRestaurantLocale("description", this) ||
      this.loadDefault("description", this)
    );
  }

  get id(): number {
    return this._commercial.id;
  }

  get name(): string {
    return (
      this.loadInCurrentLocale("name", this) ||
      this.loadInRestaurantLocale("name", this) ||
      this.loadDefault("name", this)
    );
  }

  get title(): string {
    return this._commercial.name;
  }

  get hasPrepayment(): boolean {
    return this._commercial.has_prepayment || false;
  }

  get prepaymentInfos(): string | undefined {
    if (!this.hasPrepayment) {
      return;
    }
    const _prepayment = this._commercial.prepayment;
    let _commercialKind;

    if (_prepayment && _prepayment.price) {
      const priceAndCurrency = numberFormat(
        i18n.language,
        _prepayment.price,
        _prepayment.currency
      );

      switch (this._commercial.object) {
        case "menu":
          _commercialKind = "menus";
          break;
        case "offer":
          _commercialKind = "offers";
          break;
        case "event":
          _commercialKind = "events";
          break;
      }

      const _prepaymentInfos = i18n.t(
        `instabook.${_commercialKind}.prepayment_infos.${_prepayment.payment_kind}`,
        {
          price_and_currency: priceAndCurrency,
        }
      );
      return _prepaymentInfos;
    }
  }

  loadCommercialTranslationForLocale(
    forLocale: string = this.locale
  ): CommercialTranslation | undefined {
    return this._commercial.translations
      ? this._commercial.translations.find(
          (com: CommercialTranslation) => com.locale === forLocale
        )
      : undefined;
  }

  loadInCurrentLocale(property: string, _this: this): string | undefined {
    type ObjectKey = keyof typeof _this._translationInCurrentLocale;
    const keyPpty = property as ObjectKey;
    return this._translationInCurrentLocale
      ? this._translationInCurrentLocale[keyPpty]
      : undefined;
  }

  loadInRestaurantLocale(property: string, _this: this): string | undefined {
    type ObjectKey = keyof typeof _this._translationInRestaurantLocale;
    const keyPpty = property as ObjectKey;
    return this._translationInRestaurantLocale
      ? this._translationInRestaurantLocale[keyPpty]
      : undefined;
  }

  loadDefault(property: string, _this: this) {
    type ObjectKey = keyof typeof _this._commercial;
    const keyPpty = property as ObjectKey;
    return this._commercial[keyPpty] ?? "";
  }
}
